import { useCallback, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector, batch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

// Redux actions
import { setselectedContactID } from '../redux/Slice/Inbox/inboxAllSlice';
import {
  fetchMessagesByContact,
  fetchMessagesByGroup,
  clearMessages,
  setExpectedContact,
} from '../redux/Slice/Inbox/InboxMessageSlice';
import { fetchContactById, fetchGroupById, clearContactInfo, clearGroupInfo } from '../redux/Slice/Inbox/inboxInfoSlice';
import { clearUnreadCount, markGroupAsRead } from '../redux/Slice/Inbox/inboxRecentSlice';
import { setActiveChat, clearActiveChat } from '../utils/socketHandler';
import { clearCurrentChat } from '../redux/Slice/Inbox/teamMemberChatSlice';
import { navigateOffCustomerChatRoute } from '../utils/inboxTabSwitch';

const DEBOUNCE_RETRY_MS = 220;

function isDebouncedRejection(err) {
  return err === 'Debounced';
}

/**
 * RTK unwrap throws rejectWithValue payload; debounced thunks use the string "Debounced".
 */
async function unwrapThunkWithDebounceRetry(dispatch, thunk, arg, currentRequest, signal) {
  const run = () => {
    if (currentRequest.cancelled || signal.aborted) {
      throw new Error('Request cancelled');
    }
    return dispatch(thunk(arg)).unwrap();
  };
  try {
    return await run();
  } catch (e) {
    if (!isDebouncedRejection(e)) throw e;
    await new Promise((r) => setTimeout(r, DEBOUNCE_RETRY_MS));
    if (currentRequest.cancelled || signal.aborted) {
      throw new Error('Request cancelled');
    }
    return dispatch(thunk(arg)).unwrap();
  }
}

/**
 * Custom hook for unified contact selection across the application
 * Ensures all contact-related Redux states stay synchronized
 * Includes request cancellation for rapid contact switching
 */
export const useContactSelection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountid: routeAccountId, userid: routeUserId } = useParams();

  // Stable ref tracking the currently selected contact to guard against redundant fetches
  const selectedContactIdRef = useRef(null);
  const currentSelectedId = useSelector(state => state.inboxAll?.selectedContactID);
  useEffect(() => {
    selectedContactIdRef.current = currentSelectedId;
  }, [currentSelectedId]);

  // Track the current active request to cancel previous ones
  const activeRequestRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Leading-edge debounce: first click of a burst fires immediately;
  // subsequent clicks within 180ms on DIFFERENT contacts are dropped.
  // Same-contact clicks are already guarded by selectedContactIdRef.
  const lastClickTimeRef = useRef(0);
  const lastClickContactRef = useRef(null);

  // Cancel any ongoing requests
  const cancelActiveRequests = useCallback(() => {
    // Cancel previous AbortController if exists
    if (abortControllerRef.current) {
      console.log("🚫 Cancelling previous contact requests");
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Mark previous request as cancelled
    if (activeRequestRef.current) {
      activeRequestRef.current.cancelled = true;
      activeRequestRef.current = null;
    }
  }, []);

  // Clear all contact-related states
  const clearAllContactStates = useCallback(() => {
    // Cancel any ongoing requests first
    cancelActiveRequests();
    
    // Clear Redux states
    dispatch(clearMessages());
    dispatch(clearContactInfo());
    dispatch(clearGroupInfo());

    // Clear socket active chat (not a Redux action)
    clearActiveChat();
    
    console.log("✅ All contact states cleared");
  }, [dispatch, cancelActiveRequests]);

  // Unified contact selection function with request cancellation
  const selectContact = useCallback(async (contactId, options = {}) => {
    if (!contactId) {
      console.error("selectContact: No contactId provided");
      return;
    }

    const {
      navigate: shouldNavigate = false,
      accountid = null,
      userid = null,
      clearPrevious = true,
      forceRefresh = false,
      navigateState = null,
    } = options;

    // Skip the full fetch when clicking the already-selected contact
    if (
      !forceRefresh &&
      selectedContactIdRef.current &&
      String(selectedContactIdRef.current) === String(contactId)
    ) {
      return { success: true, contactId, cached: true };
    }

    // Leading-edge debounce for rapid contact spam: drop clicks on different contacts
    // within 180ms of each other. First click always fires; only the burst tail is dropped.
    const now = Date.now();
    const isBurst =
      !forceRefresh &&
      now - lastClickTimeRef.current < 180 &&
      lastClickContactRef.current !== null &&
      lastClickContactRef.current !== String(contactId);
    if (isBurst) return;
    lastClickTimeRef.current = now;
    lastClickContactRef.current = String(contactId);

    // Eagerly mark this contact as "in progress" so that the URL-sync effect
    // in MessageCard (which fires when navigate:true changes the URL) cannot
    // pass the guard above and trigger a redundant second fetch.
    // The useEffect that syncs selectedContactIdRef from Redux only fires after
    // setselectedContactID dispatches post-fetch, which is too late.
    selectedContactIdRef.current = contactId;

    const effectiveAccountid = accountid ?? routeAccountId ?? null;
    const effectiveUserid   = userid ?? routeUserId ?? null;

    // Step 1: Cancel any previous requests immediately
    cancelActiveRequests();
    
    // Step 2: Create new request tracking
    const requestId = `${contactId}_${Date.now()}`;
    const currentRequest = { 
      contactId, 
      requestId, 
      cancelled: false,
      startTime: Date.now()
    };
    activeRequestRef.current = currentRequest;
    
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;
    
    try {
      // Step 3: Atomically clear old state AND mark new selection in ONE flushSync paint.
      // This guarantees the card highlights on the exact frame the user clicks — no
      // intermediate render where both old selection is gone and new one hasn't arrived yet.
      flushSync(() => {
        batch(() => {
          if (clearPrevious) {
            dispatch(clearMessages());
            dispatch(clearContactInfo());
            dispatch(clearGroupInfo());
            dispatch(clearCurrentChat());
          }
          dispatch(setselectedContactID(contactId));
          dispatch(setExpectedContact(contactId));
        });
      });
      setActiveChat(contactId);

      // Step 4: Check if request was cancelled during clearing
      if (currentRequest.cancelled || signal.aborted) {
        return { success: false, cancelled: true, contactId };
      }

      // Contact info (name/avatar/labels for the ChatInfo panel) is fetched in parallel but
      // must NOT block or fail the chat-open flow: it's a separate, independently-timed API
      // call from fetchMessagesByContact, and gating selectedContactID on both meant the pane
      // could sit fully loaded-but-unselected whenever contact-info was even slightly slower
      // (ChatBox's contactSelected/contactExists checks fall out of sync, and ChatBody's own
      // "no contact selected" effect wipes the just-fetched messages). ChatInfo already
      // tolerates contact info arriving after selectedContactID commits (socket-driven
      // selection and ChatBody's own fallback path already do this), so this is not a new
      // pattern — it just closes the gap for the click-to-open path too.
      unwrapThunkWithDebounceRetry(
        dispatch,
        fetchContactById,
        contactId,
        currentRequest,
        signal
      ).catch((err) => {
        if (isDebouncedRejection(err) || currentRequest.cancelled || signal.aborted) {
          return;
        }
        console.error(`Error loading contact info for ${contactId}:`, err);
      });

      await unwrapThunkWithDebounceRetry(
        dispatch,
        fetchMessagesByContact,
        { contactId },
        currentRequest,
        signal
      );

      // Final cancellation check before committing selection / URL
      if (currentRequest.cancelled || signal.aborted) {
        console.log(`🚫 Request ${requestId} cancelled after data fetch`);
        return { success: false, cancelled: true, contactId };
      }

      if (activeRequestRef.current?.requestId !== requestId) {
        console.log(`🚫 Request ${requestId} superseded by newer request`);
        return { success: false, superseded: true, contactId };
      }

      const loadTime = Date.now() - currentRequest.startTime;
      console.log(`✅ Contact ${contactId} fully loaded and synchronized in ${loadTime}ms`);

      // setselectedContactID and setActiveChat were already dispatched before the fetch.
      // Only clear unread count now that we know the fetch succeeded.
      dispatch(clearUnreadCount(contactId));

      if (shouldNavigate && effectiveAccountid && effectiveUserid) {
        navigate(
          `/home/${effectiveAccountid}/inbox/${effectiveUserid}/chat/${contactId}`,
          navigateState ? { state: navigateState } : undefined
        );
      }

      if (activeRequestRef.current?.requestId === requestId) {
        activeRequestRef.current = null;
      }

      return { success: true, contactId, loadTime };
    } catch (error) {
      if (error.name === 'AbortError' || currentRequest.cancelled) {
        console.log(`🚫 Request ${requestId} cancelled:`, error.message);
        return { success: false, cancelled: true, contactId };
      }
      if (error && error.message === 'Request cancelled') {
        return { success: false, cancelled: true, contactId };
      }

      console.error(`❌ Error selecting contact ${contactId}:`, error);

      if (activeRequestRef.current?.requestId === requestId) {
        activeRequestRef.current = null;
      }

      // Silent failure: clear messages/contact info but only deselect if this contact
      // is still the current one (a faster click may have already moved to another contact).
      batch(() => {
        dispatch(clearMessages());
        dispatch(clearContactInfo());
        // Only reset selectedContactID if no newer selection has superseded this request
        if (activeRequestRef.current === null || activeRequestRef.current?.contactId === contactId) {
          dispatch(setselectedContactID(null));
        }
      });
      clearActiveChat();

      if (effectiveAccountid && effectiveUserid) {
        navigateOffCustomerChatRoute(navigate, effectiveAccountid, effectiveUserid);
      }

      return { success: false, error, contactId };
    }
  }, [dispatch, navigate, cancelActiveRequests, routeAccountId, routeUserId]);

  // Group analogue of selectContact above (WhatsApp Groups plan, Phase G7) -- reuses the exact
  // same debounce/cancellation/race-guard refs (selectedContactIdRef/activeRequestRef/
  // abortControllerRef): only one thread, contact or group, is ever open at a time, so tracking
  // "whichever id was selected last" generically is correct either way, matching how
  // state.messages/state.expectedContactId already work as a single shared bucket (see
  // fetchMessagesByGroup's own doc comment in InboxMessageSlice.js). Deliberately simpler than
  // selectContact: no parallel "group info" fetch exists yet (a group-scoped ChatInfo panel is
  // a disclosed gap, not built this pass) -- clearContactInfo() is still dispatched so a
  // previously-open contact's stale info panel data doesn't linger behind an open group thread.
  const selectGroup = useCallback(async (groupId, options = {}) => {
    if (!groupId) {
      console.error("selectGroup: No groupId provided");
      return;
    }

    const {
      navigate: shouldNavigate = false,
      accountid = null,
      userid = null,
      clearPrevious = true,
      forceRefresh = false,
    } = options;

    if (
      !forceRefresh &&
      selectedContactIdRef.current &&
      String(selectedContactIdRef.current) === String(groupId)
    ) {
      return { success: true, contactId: groupId, cached: true };
    }

    const now = Date.now();
    const isBurst =
      !forceRefresh &&
      now - lastClickTimeRef.current < 180 &&
      lastClickContactRef.current !== null &&
      lastClickContactRef.current !== String(groupId);
    if (isBurst) return;
    lastClickTimeRef.current = now;
    lastClickContactRef.current = String(groupId);

    selectedContactIdRef.current = groupId;

    const effectiveAccountid = accountid ?? routeAccountId ?? null;
    const effectiveUserid   = userid ?? routeUserId ?? null;

    cancelActiveRequests();

    const requestId = `${groupId}_${Date.now()}`;
    const currentRequest = { contactId: groupId, requestId, cancelled: false, startTime: Date.now() };
    activeRequestRef.current = currentRequest;

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      flushSync(() => {
        batch(() => {
          if (clearPrevious) {
            dispatch(clearMessages());
            dispatch(clearContactInfo());
            dispatch(clearGroupInfo());
            dispatch(clearCurrentChat());
          }
          dispatch(setselectedContactID(groupId));
          dispatch(setExpectedContact(groupId));
        });
      });
      setActiveChat(groupId);

      if (currentRequest.cancelled || signal.aborted) {
        return { success: false, cancelled: true, contactId: groupId };
      }

      // Group info (subject/participant list for the chat header's "Group Members" box) is
      // fetched in parallel, same non-blocking reasoning as selectContact's own
      // fetchContactById dispatch just above -- must not gate/delay the chat opening.
      dispatch(fetchGroupById(groupId)).catch(() => {});

      await unwrapThunkWithDebounceRetry(
        dispatch,
        fetchMessagesByGroup,
        { groupId },
        currentRequest,
        signal
      );

      if (currentRequest.cancelled || signal.aborted) {
        return { success: false, cancelled: true, contactId: groupId };
      }
      if (activeRequestRef.current?.requestId !== requestId) {
        return { success: false, superseded: true, contactId: groupId };
      }

      dispatch(clearUnreadCount(groupId));
      // Persist the read state too, not just the local badge zero-out above -- see
      // markGroupAsRead's own doc comment (inboxRecentSlice.js) for why the local-only clear
      // wasn't enough (the badge kept reverting to the real, never-decreasing DB count).
      dispatch(markGroupAsRead(groupId)).catch(() => {});

      if (shouldNavigate && effectiveAccountid && effectiveUserid) {
        navigate(`/home/${effectiveAccountid}/inbox/${effectiveUserid}/chat/${groupId}`);
      }

      if (activeRequestRef.current?.requestId === requestId) {
        activeRequestRef.current = null;
      }

      return { success: true, contactId: groupId };
    } catch (error) {
      if (error.name === 'AbortError' || currentRequest.cancelled) {
        return { success: false, cancelled: true, contactId: groupId };
      }
      if (error && error.message === 'Request cancelled') {
        return { success: false, cancelled: true, contactId: groupId };
      }

      console.error(`❌ Error selecting group ${groupId}:`, error);

      if (activeRequestRef.current?.requestId === requestId) {
        activeRequestRef.current = null;
      }

      batch(() => {
        dispatch(clearMessages());
        dispatch(clearContactInfo());
        if (activeRequestRef.current === null || activeRequestRef.current?.contactId === groupId) {
          dispatch(setselectedContactID(null));
        }
      });
      clearActiveChat();

      if (effectiveAccountid && effectiveUserid) {
        navigateOffCustomerChatRoute(navigate, effectiveAccountid, effectiveUserid);
      }

      return { success: false, error, contactId: groupId };
    }
  }, [dispatch, navigate, cancelActiveRequests, routeAccountId, routeUserId]);

  // Clear contact selection
  const clearContactSelection = useCallback(() => {
    console.log("🧹 Clearing contact selection");
    
    // Cancel any ongoing requests
    cancelActiveRequests();
    
    // Clear all states
    clearAllContactStates();
    
    // Clear selected contact ID
    dispatch(setselectedContactID(null));
    
    console.log("✅ Contact selection cleared");
  }, [dispatch, clearAllContactStates, cancelActiveRequests]);

  return {
    selectContact,
    selectGroup,
    clearContactSelection,
    clearAllContactStates,
    cancelActiveRequests
  };
};

export default useContactSelection;
